'use client';

import React from 'react';

import {useCommonData} from '@udemy-v2/common-data';
import {
    DiscoveryUnits,
    DiscoveryUnitsSkeleton,
    PAGE_TYPE_TO_CONTEXT_ID,
} from '@udemy-v2/react-discovery-units';
import {useFeatures} from '@udemy-v2/react-experimentation';

import {LOHP_VERTICAL_MOBILE_RECO_UNITS, SHOW_VERNACULAR_CONTENT} from '@/app/lib/experimentation';

import type {MobileCardOptionsConfig} from '@udemy-v2/react-discovery-units';

/**
 * Mobile card options from the LOHP_VERTICAL_MOBILE_RECO_UNITS experiment. The shared
 * `DiscoveryUnits` component never reads experiment flags itself — this app resolves its own
 * config and passes it down.
 */
function useMobileCardOptionsConfig(): MobileCardOptionsConfig | undefined {
    const {featureVariantAssignments} = useFeatures([LOHP_VERTICAL_MOBILE_RECO_UNITS]);

    return featureVariantAssignments.get(LOHP_VERTICAL_MOBILE_RECO_UNITS)?.configuration as
        | MobileCardOptionsConfig
        | undefined;
}

/**
 * Above-the-fold discovery units — always renders, mirrors v1's unconditional
 * `discovery-units-top` instance.
 */
export const DiscoveryUnitsDefaultPosition: React.FC = () => {
    const {
        visitor: {isFirstTimeVisitor},
    } = useCommonData();
    const mobileCardOptionsConfig = useMobileCardOptionsConfig();

    if (isFirstTimeVisitor === undefined) {
        return <DiscoveryUnitsSkeleton />;
    }

    const contextId = isFirstTimeVisitor
        ? PAGE_TYPE_TO_CONTEXT_ID.logged_out_homepage
        : PAGE_TYPE_TO_CONTEXT_ID.personalized_home;

    return (
        <DiscoveryUnits
            contextId={contextId}
            dataPurpose="discovery-units"
            mobileCardOptionsConfig={mobileCardOptionsConfig}
        />
    );
};

/**
 * Vernacular discovery units — a localized-language recommendations block, gated by
 * the `show_vernacular_content` experiment. Mirrors v1.
 */
export const DiscoveryUnitsVernacularPosition: React.FC = () => {
    const {featureVariantAssignments} = useFeatures([SHOW_VERNACULAR_CONTENT]);
    const mobileCardOptionsConfig = useMobileCardOptionsConfig();

    const showVernacularContent = Boolean(
        featureVariantAssignments.get(SHOW_VERNACULAR_CONTENT)?.configuration
            ?.showVernacularContent,
    );

    if (!showVernacularContent) return null;

    return (
        <DiscoveryUnits
            contextId={PAGE_TYPE_TO_CONTEXT_ID.vernacular_lohp}
            dataPurpose="discovery-units-vernacular"
            uiRegion="vernacular_lohp"
            mobileCardOptionsConfig={mobileCardOptionsConfig}
        />
    );
};
